import { createClient } from '@sanity/client';
import imageUrlBuilder from '@sanity/image-url';

export const client = createClient({
  projectId: process.env.NEXT_PUBLIC_SANITY_PROJECT_ID || 'cwdo0dfy',
  dataset: process.env.NEXT_PUBLIC_SANITY_DATASET || 'production',
  apiVersion: process.env.NEXT_PUBLIC_SANITY_API_VERSION || '2024-01-01',
  useCdn: process.env.NODE_ENV === 'production', // Disable CDN in development for fresh data
  // token: process.env.SANITY_API_READ_TOKEN, // Only if you need to fetch draft content

  // Add perspective for development
  perspective: process.env.NODE_ENV === 'development' ? 'raw' : 'published',

  // Disable caching in development
  ...(process.env.NODE_ENV === 'development' && {
    ignoreBrowserTokenWarning: true,
    withCredentials: true,
  }),
});

// Draft-enabled client for preview mode
export const draftClient = createClient({
  projectId: process.env.NEXT_PUBLIC_SANITY_PROJECT_ID || 'cwdo0dfy',
  dataset: process.env.NEXT_PUBLIC_SANITY_DATASET || 'production',
  apiVersion: process.env.NEXT_PUBLIC_SANITY_API_VERSION || '2024-01-01',
  useCdn: false, // Always disable CDN for draft content
  token: process.env.SANITY_API_READ_TOKEN, // Required for draft content
  perspective: 'previewDrafts', // Include draft documents
  ignoreBrowserTokenWarning: true,
});

const builder = imageUrlBuilder(client);

export function urlFor(source: any) {
  return builder.image(source);
}

/**
 * Public CDN URL for a Sanity file asset from its reference, e.g.
 * `file-<hash>-glb` -> https://cdn.sanity.io/files/<project>/<dataset>/<hash>.glb
 * Lets content blocks carry a plain reference without a GROQ dereference.
 */
export function fileUrlFromRef(ref?: string | null): string | null {
  const m = /^file-([a-f0-9]+)-([a-z0-9]+)$/.exec(ref || '');
  if (!m) return null;
  const { projectId, dataset } = client.config();
  return `https://cdn.sanity.io/files/${projectId}/${dataset}/${m[1]}.${m[2]}`;
}

// Types for blog posts
export interface BlogPost {
  _id: string;
  _type: 'post';
  title: string;
  slug: {
    current: string;
  };
  excerpt?: string;
  content: any[];
  categories?: string[];
  mainImage?: {
    asset: {
      _ref: string;
    };
    alt?: string;
  };
  author?: {
    name: string;
    slug: {
      current: string;
    };
    image?: {
      asset: {
        _ref: string;
      };
    };
  };
  publishedAt: string;
  _createdAt: string;
  _updatedAt: string;
}

export interface Author {
  _id: string;
  _type: 'author';
  name: string;
  slug: {
    current: string;
  };
  image?: {
    asset: {
      _ref: string;
    };
  };
  bio?: string;
}

// Category utilities
export const BLOG_CATEGORIES = {
  featured: 'Featured',
  artists: 'Artists',
  partnerships: 'Partnerships',
  stories: 'Stories',
  engineering: 'Engineering',
} as const;

export type BlogCategory = keyof typeof BLOG_CATEGORIES;

export function getCategoryLabel(category: string): string {
  return BLOG_CATEGORIES[category as BlogCategory] || category;
}

// Guide types

// Shape returned by guide listing/related projections
export interface GuideSummary {
  _id: string;
  title: string;
  slug: {
    current: string;
  };
  heroDescription: string;
  cardImage?: {
    asset: {
      _id: string;
      url: string;
    };
    alt?: string;
  };
  _createdAt: string;
}

export interface Guide {
  _id: string;
  _type: 'guide';
  title: string;
  slug: {
    current: string;
  };
  cardImage: {
    asset: {
      _ref: string;
    };
    alt?: string;
  };
  heroDescription: string;
  heroImage: {
    asset: {
      _ref: string;
    };
    alt?: string;
  };
  videoUrl?: string;
  jumpMenu?: Array<{
    label: string;
    anchor: string;
  }>;
  content: any[];
  relatedGuides?: GuideSummary[];
  toneStream?: {
    title?: string;
    filter: 'none' | 'nam' | 'ir';
  };
  _createdAt: string;
  _updatedAt: string;
}
